import { subDays, isSameDay, isAfter } from 'date-fns';
import { Patient } from '@/types';

export interface PatientFilterOptions {
  showAll?: boolean;
  daysBack?: number;
  includeActive?: boolean;
  includeFollowUps?: boolean | 'todayOnly';
  includePendingPayments?: boolean;
}

export const getSmartFilteredPatients = (
  patients: Patient[], 
  options: PatientFilterOptions = {}
) => {
  const {
    showAll = false,
    daysBack = 2,
    includeActive = true,
    includeFollowUps = true,
    includePendingPayments = true
  } = options;

  // If showAll is true, return all patients
  if (showAll) {
    return patients;
  }

  const cutoffDate = subDays(new Date(), daysBack);
  const today = new Date();

  return patients.filter(patient => {
    const patientDate = new Date(patient.registeredAt);
    
    // 1. Show patients from last N days
    if (patientDate >= cutoffDate) {
      return true;
    }
    
    // 2. Show active patients regardless of date
    if (includeActive && (
      patient.status === 'waiting' || 
      patient.status === 'in-consultation'
    )) {
      return true;
    }
    
    // 3. Show follow-up patients
    if (includeFollowUps && patient.followUpDate) {
      const followUpDate = new Date(patient.followUpDate);
      if (includeFollowUps === 'todayOnly') {
        // Admin/Staff: Only today's follow-ups
        if (isSameDay(followUpDate, today)) {
          return true;
        }
      } else {
        // Doctors: All future follow-ups
        if (isSameDay(followUpDate, today) || isAfter(followUpDate, today)) {
          return true;
        }
      }
    }
    
    // 4. Show patients with pending payments
    if (includePendingPayments) {
      const totalCost = patient.services?.reduce((sum, service) => 
        sum + (Number(service.price) || 0), 0
      ) || 0;
      if (patient.amountPaid < totalCost) {
        return true;
      }
    }
    
    return false;
  });
};

// Helper function to get total cost of patient services
export const getPatientTotalCost = (patient: Patient): number => {
  return patient.services?.reduce((sum, service) => 
    sum + (Number(service.price) || 0), 0
  ) || 0;
};

// Helper function to check if patient has pending payments
export const hasPendingPayments = (patient: Patient): boolean => {
  const totalCost = getPatientTotalCost(patient);
  return patient.amountPaid < totalCost;
};

// Helper function to check if patient is active
export const isActivePatient = (patient: Patient): boolean => {
  return patient.status === 'waiting' || patient.status === 'in-consultation';
};

// Helper function to check if patient has upcoming follow-up
export const hasUpcomingFollowUp = (patient: Patient): boolean => {
  if (!patient.followUpDate) return false;
  const followUpDate = new Date(patient.followUpDate);
  const today = new Date();
  return isSameDay(followUpDate, today) || isAfter(followUpDate, today);
}; 